import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { Mail, Lock, LogIn } from 'lucide-react';
import BrandMark from '@/components/BrandMark';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, login } = useUser();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for auth message and auto-redirect
  useEffect(() => {
    // Check for message from auth callback
    const authMessage = sessionStorage.getItem('auth_message');
    if (authMessage) {
      setError(authMessage);
      sessionStorage.removeItem('auth_message');
    }
    
    // Auto-redirect if already logged in
    if (user) {
      if (user.role === 'master_admin') {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.role === 'coach') {
        navigate('/coach/dashboard', { replace: true });
      } else {
        navigate('/client/hub', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    
    // Use XMLHttpRequest to avoid any fetch interceptors
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/auth/login`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.withCredentials = true;
    
    xhr.onload = function() {
      setLoading(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 400) {
          setError(data.detail || 'Login failed');
          return;
        }
        
        if (!data.user) {
          setError('Invalid response from server');
          return;
        }
        
        // Save user and token
        login(data.user, data.access_token);
        
        // Navigate based on role
        if (data.user.role === 'master_admin') {
          navigate('/admin/dashboard');
        } else if (data.user.role === 'coach') {
          navigate('/coach/dashboard');
        } else {
          navigate('/client/hub');
        }
      } catch (parseError) {
        setError('Login failed. Please try again.');
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      setError('Network error. Please try again.');
    };
    
    xhr.send(JSON.stringify(formData));
  };

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4 pb-20">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-obsidian-night mb-2">GNTL STRNGTH</h1>
          <p className="text-shadow-pine font-body text-sm sm:text-base">Welcome back</p>
        </div>

        <Card className="bg-white border-burnt-terra/20">
          <CardHeader className="pb-4 sm:pb-6">
            <CardTitle className="font-display text-obsidian-night text-center text-lg sm:text-xl">Sign In</CardTitle>
            <CardDescription className="text-shadow-pine font-body text-center text-sm">
              Enter your credentials to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-obsidian-night font-body text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night text-base"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-obsidian-night font-body text-sm">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-burnt-terra hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-shadow-pine/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                  />
                </div>
                <p className="text-xs text-shadow-pine/60">
                  First time? Use your email as password.
                </p>
              </div>

              {error && (
                <div className="text-red-600 text-sm font-body text-center bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
              >
                {loading ? 'Signing in...' : 'Sign In'}
                <LogIn className="w-4 h-4 ml-2" />
              </Button>
            </form>

            <p className="text-center text-sm text-shadow-pine/60 font-body mt-6">
              Don't have an account?{' '}
              <Link to="/signup" className="text-burnt-terra hover:underline font-semibold">
                Sign up
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
      <BrandMark />
    </div>
  );
};

export default LoginPage;
